// admin/src/pages/payments/PaymentsCommission.jsx
import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  Container,
  Row,
  Col,
  Table,
  Button,
  Form,
  Spinner,
  Alert,
  FormControl,
  Dropdown,
} from "react-bootstrap";
import Swal from "sweetalert2";
import withReactContent from "sweetalert2-react-content";
import api from "../services/api";
import { formatCurrency } from "../utils/format";
import { Colors } from "../constants/Colors";
import {
  PER_PAGE,
  PaymentsStyles,
  StatsRow,
  PaginationBar,
  PaymentDetailsModal,
  StatusPill,
  MethodPill,
  RatePill,
  RefundBadge,
  Avatar,
  getRefundState,
  getRefundInfo,
  isRefunded,
  getPaymentId,
  getPaymentDate,
  getPassengerName,
  getOperatorName,
  getJeepneyLabel,
  formatShortDate,
  getDefaultDateRange,
  parseList,
} from "../utils/paymentShared";

const MySwal = withReactContent(Swal);

const STATUS_OPTIONS = [
  { value: "succeeded", label: "Succeeded" },
  { value: "pending", label: "Pending" },
  { value: "failed", label: "Failed" },
  { value: "refunded", label: "Refunded" },
  { value: "all", label: "All Status" },
];

const METHOD_OPTIONS = [
  { value: "all", label: "All Methods" },
  { value: "gcash", label: "GCash" },
  { value: "qrph", label: "QR Ph" },
  { value: "cash", label: "Cash" },
  { value: "manual", label: "Manual" },
];

// Cancellation fees are platform penalty income, not commission, so the report
// (and the backend) always excludes them. The old "Cancellation Fee" option here
// could only ever return an empty list.
const TYPE_OPTIONS = [
  { value: "all", label: "All Types" },
  { value: "rental", label: "Rental (Active)" },
  { value: "rental_refunded", label: "Refunds (incl. pending)" },
];

const EMPTY_TOTALS = {
  total_earnings: 0,
  total_commission: 0,
  total_operator_earnings: 0,
  total_transactions: 0,
  net_earnings: 0,
  net_commission: 0,
  net_operator_earnings: 0,
  net_transactions: 0,
  refunded_amount: 0,
  refunded_commission: 0,
  refunded_operator_earnings: 0,
  refunded_transactions: 0,
};

/** Fallback when the backend does not send `totals` (current page only). */
const computeTotals = (rows) => {
  const t = { ...EMPTY_TOTALS };
  rows.forEach((p) => {
    const refunded = isRefunded(p);
    const amt = parseFloat(p.amount || 0);
    const com = parseFloat(p.commission_amount || 0);
    const earn = parseFloat(p.operator_earnings || 0);
    t.total_earnings += amt;
    t.total_commission += com;
    t.total_operator_earnings += earn;
    t.total_transactions += 1;
    if (refunded) {
      t.refunded_amount += amt;
      t.refunded_commission += com;
      t.refunded_operator_earnings += earn;
      t.refunded_transactions += 1;
    } else {
      t.net_earnings += amt;
      t.net_commission += com;
      t.net_operator_earnings += earn;
      t.net_transactions += 1;
    }
  });
  return t;
};

const labelOf = (options, value, fallback) =>
  options.find((o) => o.value === value)?.label || fallback;

const PaymentsCommission = () => {
  const defaults = getDefaultDateRange();

  const [dateFrom, setDateFrom] = useState(defaults.from);
  const [dateTo, setDateTo] = useState(defaults.to);
  const [statusFilter, setStatusFilter] = useState("succeeded");
  const [methodFilter, setMethodFilter] = useState("all");
  const [typeFilter, setTypeFilter] = useState("all");

  const [commissions, setCommissions] = useState([]);
  const [totals, setTotals] = useState(EMPTY_TOTALS);
  const [pagination, setPagination] = useState({
    current_page: 1,
    last_page: 1,
    per_page: PER_PAGE,
    total: 0,
  });

  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState(null);

  const [selected, setSelected] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);

  const requestIdRef = useRef(0);
  const viewLockRef = useRef(false);

  const loadCommissions = useCallback(
    async (page = 1, overrides = {}) => {
      const reqId = ++requestIdRef.current;

      const params = {
        page,
        per_page: PER_PAGE,
        date_from: overrides.dateFrom ?? dateFrom,
        date_to: overrides.dateTo ?? dateTo,
        status: overrides.statusFilter ?? statusFilter,
        payment_method: overrides.methodFilter ?? methodFilter,
        payment_type: overrides.typeFilter ?? typeFilter,
      };

      Object.keys(params).forEach((k) => {
        if (
          params[k] === "" ||
          params[k] === "all" ||
          params[k] === undefined ||
          params[k] === null
        ) {
          delete params[k];
        }
      });

      try {
        setLoading(true);
        setError(null);
        const res = await api.get("/payments/commissions", { params });
        if (reqId !== requestIdRef.current) return;

        const { rows, pagination: pg, error: hasError } = parseList(res.data);

        if (hasError) {
          setError(res.data?.message || "Failed to load commission data");
          setCommissions([]);
          setTotals(EMPTY_TOTALS);
          return;
        }

        // The backend applies every filter, and its totals cover ALL pages.
        // (The old client-side type filter ran after pagination and also dropped
        // rows with a pending refund that the backend had deliberately included.)
        setCommissions(rows);
        setTotals({
          ...EMPTY_TOTALS,
          ...(res.data?.totals || computeTotals(rows)),
        });
        setPagination(
          pg || {
            current_page: page,
            last_page: 1,
            per_page: PER_PAGE,
            total: rows.length,
          },
        );
      } catch (err) {
        if (reqId !== requestIdRef.current) return;
        setError(
          err.response?.data?.message ||
            err.message ||
            "Failed to load commission data.",
        );
        setCommissions([]);
        setTotals(EMPTY_TOTALS);
      } finally {
        if (reqId === requestIdRef.current) setLoading(false);
      }
    },
    [dateFrom, dateTo, statusFilter, methodFilter, typeFilter],
  );

  useEffect(() => {
    loadCommissions(1);
  }, [loadCommissions]);

  const handleApplyFilters = (e) => {
    if (e) e.preventDefault();
    loadCommissions(1);
  };

  const handleResetFilters = () => {
    const d = getDefaultDateRange();
    setDateFrom(d.from);
    setDateTo(d.to);
    setStatusFilter("succeeded");
    setMethodFilter("all");
    setTypeFilter("all");
    loadCommissions(1, {
      dateFrom: d.from,
      dateTo: d.to,
      statusFilter: "succeeded",
      methodFilter: "all",
      typeFilter: "all",
    });
  };

  const handlePageChange = (n) => {
    if (n >= 1 && n <= pagination.last_page) loadCommissions(n);
  };

  const exportReport = async () => {
    try {
      setExporting(true);
      const res = await api.get("/payments/commissions/export", {
        params: {
          date_from: dateFrom,
          date_to: dateTo,
          status: statusFilter !== "all" ? statusFilter : undefined,
          payment_method: methodFilter !== "all" ? methodFilter : undefined,
          payment_type: typeFilter !== "all" ? typeFilter : undefined,
        },
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(
        new Blob([res.data], { type: "text/csv" }),
      );
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute(
        "download",
        `commission_report_${dateFrom}_to_${dateTo}.csv`,
      );
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      MySwal.fire({
        icon: "success",
        title: "Export Successful",
        text: "Commission report downloaded.",
        timer: 2500,
        showConfirmButton: false,
      });
    } catch (err) {
      MySwal.fire({
        icon: "error",
        title: "Export Failed",
        text:
          err.response?.data?.message || "Failed to export commission report.",
        confirmButtonColor: Colors.PRIMARY,
      });
    } finally {
      setExporting(false);
    }
  };

  const viewDetails = (payment) => {
    if (viewLockRef.current) return;
    viewLockRef.current = true;
    setSelected(payment);
    setDetailOpen(true);
    setTimeout(() => {
      viewLockRef.current = false;
    }, 400);
  };

  const getRowClass = (p) => {
    const state = getRefundState(p);
    if (state === "pending") return "row-refund-pending";
    if (state === "refunded") return "row-refunded";
    return "";
  };

  /* ---------------- stats ---------------- */
  const avgRate =
    totals.net_earnings > 0
      ? ((totals.net_commission / totals.net_earnings) * 100).toFixed(1)
      : "0.0";

  const statCards = [
    {
      k: "Net Revenue",
      v: formatCurrency(totals.net_earnings),
      i: "bi-cash-stack",
      c: Colors.PRIMARY,
      isText: true,
      title: `${totals.net_transactions} active transaction(s)`,
    },
    {
      k: "Net Commission",
      v: formatCurrency(totals.net_commission),
      i: "bi-graph-up-arrow",
      c: Colors.INFO,
      isText: true,
      title: `Gross ${formatCurrency(totals.total_commission)} minus ${formatCurrency(totals.refunded_commission)} on refunds`,
    },
    {
      k: "Operator Earnings",
      v: formatCurrency(totals.net_operator_earnings),
      i: "bi-wallet2",
      c: Colors.SUCCESS,
      isText: true,
      title: `Gross ${formatCurrency(totals.total_operator_earnings)} minus ${formatCurrency(totals.refunded_operator_earnings)} clawed back`,
    },
    {
      k: "Refunded",
      v: formatCurrency(totals.refunded_amount),
      i: "bi-arrow-counterclockwise",
      c: Colors.RED,
      isText: true,
      title: `${totals.refunded_transactions} refunded transaction(s)`,
    },
    {
      k: "Transactions",
      v: Number(totals.total_transactions).toLocaleString(),
      i: "bi-receipt",
      c: Colors.WARNING,
      title: `${totals.net_transactions} net · ${totals.refunded_transactions} refunded`,
    },
    {
      k: "Avg Commission",
      v: `${avgRate}%`,
      i: "bi-percent",
      c: Colors.SECONDARY_BLUE,
      title: "Net commission as a share of net revenue",
    },
  ];

  return (
    <div className="bk-page">
      <PaymentsStyles />
      <Container fluid>
        <div className="bk-header">
          <div className="position-relative" style={{ zIndex: 1 }}>
            <h2>Payments &amp; Commission</h2>
            <p>
              Track passenger payments, platform commission, and operator
              earnings.
            </p>
          </div>
        </div>

        {error && (
          <Alert variant="danger" dismissible onClose={() => setError(null)}>
            <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
              <div>
                <i className="bi bi-exclamation-triangle me-2" />
                {error}
              </div>
              <Button
                className="bk-btn-ghost"
                size="sm"
                onClick={() => loadCommissions(pagination.current_page)}
              >
                <i className="bi bi-arrow-clockwise me-1" />
                Retry
              </Button>
            </div>
          </Alert>
        )}

        <StatsRow cards={statCards} loading={loading} />

        {/* ---------- Filters ---------- */}
        <div className="bk-toolbar">
          <Form onSubmit={handleApplyFilters}>
            <Row className="g-2 align-items-end">
              <Col xs={6} md={3} lg={2}>
                <label className="bk-input-label">From</label>
                <FormControl
                  type="date"
                  className="bk-input"
                  value={dateFrom}
                  max={dateTo}
                  onChange={(e) => setDateFrom(e.target.value)}
                />
              </Col>
              <Col xs={6} md={3} lg={2}>
                <label className="bk-input-label">To</label>
                <FormControl
                  type="date"
                  className="bk-input"
                  value={dateTo}
                  min={dateFrom}
                  onChange={(e) => setDateTo(e.target.value)}
                />
              </Col>
              <Col xs={6} md={3} lg={2}>
                <label className="bk-input-label">Status</label>
                <Dropdown>
                  <Dropdown.Toggle className="bk-filter-btn w-100">
                    <span>
                      <i className="bi bi-funnel me-2" />
                      {labelOf(STATUS_OPTIONS, statusFilter, "Status")}
                    </span>
                  </Dropdown.Toggle>
                  <Dropdown.Menu className="w-100">
                    {STATUS_OPTIONS.map((o) => (
                      <Dropdown.Item
                        key={o.value}
                        active={statusFilter === o.value}
                        onClick={() => setStatusFilter(o.value)}
                      >
                        {o.label}
                      </Dropdown.Item>
                    ))}
                  </Dropdown.Menu>
                </Dropdown>
              </Col>
              <Col xs={6} md={3} lg={2}>
                <label className="bk-input-label">Method</label>
                <Dropdown>
                  <Dropdown.Toggle className="bk-filter-btn w-100">
                    <span>
                      <i className="bi bi-credit-card me-2" />
                      {labelOf(METHOD_OPTIONS, methodFilter, "Method")}
                    </span>
                  </Dropdown.Toggle>
                  <Dropdown.Menu className="w-100">
                    {METHOD_OPTIONS.map((o) => (
                      <Dropdown.Item
                        key={o.value}
                        active={methodFilter === o.value}
                        onClick={() => setMethodFilter(o.value)}
                      >
                        {o.label}
                      </Dropdown.Item>
                    ))}
                  </Dropdown.Menu>
                </Dropdown>
              </Col>
              <Col xs={6} md={3} lg={2}>
                <label className="bk-input-label">Type</label>
                <Dropdown>
                  <Dropdown.Toggle className="bk-filter-btn w-100">
                    <span>
                      <i className="bi bi-tag me-2" />
                      {labelOf(TYPE_OPTIONS, typeFilter, "Type")}
                    </span>
                  </Dropdown.Toggle>
                  <Dropdown.Menu className="w-100">
                    {TYPE_OPTIONS.map((o) => (
                      <Dropdown.Item
                        key={o.value}
                        active={typeFilter === o.value}
                        onClick={() => setTypeFilter(o.value)}
                      >
                        {o.label}
                      </Dropdown.Item>
                    ))}
                  </Dropdown.Menu>
                </Dropdown>
              </Col>
              <Col xs={6} md={3} lg={2}>
                <div className="d-flex gap-2">
                  <Button
                    type="submit"
                    className="bk-btn-primary flex-grow-1"
                    disabled={loading}
                    style={{ height: 42 }}
                  >
                    {loading ? (
                      <Spinner animation="border" size="sm" />
                    ) : (
                      "Apply"
                    )}
                  </Button>
                  <Button
                    type="button"
                    className="bk-btn-ghost"
                    onClick={handleResetFilters}
                    disabled={loading}
                    title="Reset filters"
                    style={{ height: 42 }}
                  >
                    <i className="bi bi-arrow-counterclockwise" />
                  </Button>
                </div>
              </Col>
            </Row>
          </Form>
        </div>

        {/* ---------- Table ---------- */}
        <div className="bk-card">
          <div className="bk-card-header">
            <h5>
              <i className="bi bi-list-ul" style={{ color: Colors.PRIMARY }} />
              Transaction Log
            </h5>
            <div className="d-flex align-items-center gap-3 flex-wrap">
              <small>
                {pagination.total > 0
                  ? `Page ${pagination.current_page} of ${pagination.last_page} · ${pagination.total} total · cancellation fees excluded`
                  : "No records"}
              </small>
              <Button
                className="bk-btn-ghost"
                type="button"
                onClick={exportReport}
                disabled={exporting || loading}
              >
                {exporting ? (
                  <>
                    <Spinner
                      as="span"
                      animation="border"
                      size="sm"
                      className="me-2"
                    />
                    Exporting…
                  </>
                ) : (
                  <>
                    <i className="bi bi-download me-1" />
                    Export CSV
                  </>
                )}
              </Button>
            </div>
          </div>

          {loading ? (
            <div style={{ padding: 20 }}>
              {[...Array(6)].map((_, i) => (
                <div key={i} className="bk-skel mb-3" style={{ height: 42 }} />
              ))}
            </div>
          ) : commissions.length === 0 ? (
            <div className="bk-empty">
              <div className="bk-empty-icon">
                <i className="bi bi-inbox" />
              </div>
              <h5 style={{ color: Colors.TEXT_DARK }}>No transactions found</h5>
              <p className="text-muted mb-0">
                Try widening the date range or clearing filters.
              </p>
            </div>
          ) : (
            <>
              <Table responsive hover className="bk-table">
                <thead>
                  <tr>
                    <th>Payment</th>
                    <th>Passenger</th>
                    <th>Operator</th>
                    <th>Jeepney</th>
                    <th>Method</th>
                    <th style={{ textAlign: "right" }}>Amount</th>
                    <th style={{ textAlign: "right" }}>Commission</th>
                    <th style={{ textAlign: "right" }}>Operator Net</th>
                    <th>Status</th>
                    <th>Date</th>
                    <th className="col-actions">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {commissions.map((p) => {
                    const refundState = getRefundState(p);
                    const refunded = refundState === "refunded";
                    const passenger = getPassengerName(p);
                    const muted = refunded ? "muted" : "";

                    return (
                      <tr
                        key={getPaymentId(p)}
                        className={getRowClass(p)}
                        onClick={() => viewDetails(p)}
                      >
                        <td>
                          <span className="bk-id-chip">#{getPaymentId(p)}</span>
                          <div className="bk-sub">Rental #{p.rental_id}</div>
                        </td>
                        <td>
                          <div className="d-flex align-items-center gap-2">
                            <Avatar name={passenger} />
                            <div className="bk-name">{passenger}</div>
                          </div>
                        </td>
                        <td>{getOperatorName(p)}</td>
                        <td>{getJeepneyLabel(p)}</td>
                        <td>
                          <MethodPill method={p.payment_method} />
                        </td>
                        <td style={{ textAlign: "right" }}>
                          <span className={`bk-money ${muted}`}>
                            {formatCurrency(p.amount)}
                          </span>
                        </td>
                        <td style={{ textAlign: "right" }}>
                          <span className={`bk-money ${muted}`}>
                            {formatCurrency(p.commission_amount)}
                          </span>
                          <div>
                            <RatePill rate={p.commission_rate} />
                          </div>
                        </td>
                        <td style={{ textAlign: "right" }}>
                          <span className={`bk-money ${muted}`}>
                            {formatCurrency(p.operator_earnings)}
                          </span>
                        </td>
                        <td>
                          <div className="d-flex flex-column align-items-start gap-1">
                            <StatusPill
                              status={refunded ? "refunded" : p.status}
                            />
                            <RefundBadge
                              state={refundState}
                              amount={getRefundInfo(p).refund_amount}
                            />
                          </div>
                        </td>
                        <td>
                          <span
                            style={{
                              color: Colors.TEXT_LIGHT,
                              fontSize: 13,
                              whiteSpace: "nowrap",
                            }}
                          >
                            {formatShortDate(getPaymentDate(p))}
                          </span>
                        </td>
                        <td
                          className="col-actions"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <Button
                            className="bk-btn-ghost"
                            size="sm"
                            onClick={() => viewDetails(p)}
                            title="View details"
                          >
                            <i className="bi bi-eye me-1" />
                            View
                          </Button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </Table>

              <PaginationBar
                pagination={pagination}
                loading={loading}
                onPage={handlePageChange}
              />
            </>
          )}
        </div>

        <PaymentDetailsModal
          payment={selected}
          show={detailOpen}
          onHide={() => setDetailOpen(false)}
        />
      </Container>
    </div>
  );
};

export default PaymentsCommission;
